<script lang="ts">
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Input from "@components/form/Input.svelte";
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import type { ClassValue } from "svelte/elements";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { CouponAccountValidity } from "@core/schema/CouponAccountValidity.js";
	import { CustomerType } from "@core/schema/CustomerType.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import translations from "./CouponInput.translations.json" with { type: "json" };

	const { subcategories } = getGlobalTemplateProps();
	const { class: customClass }: { class?: ClassValue } = $props();
	const translate = getTranslate(translations);
	const settings = getGlobalSettings();
	let currentCouponValue = $derived(Cart.couponCode ?? "");

	type Response = "notFound" | "noEligibleItems" | "notB2c" | true;

	const validateCoupon = createPromiseWithStatus(async function (code: string): Promise<Response> {
		const coupon = await Cart.validateCoupon(settings, code);
		if (!coupon) {
			return "notFound";
		}
		if (coupon.accountValidity === CouponAccountValidity.ForB2c && Auth.customerType !== CustomerType.B2c) {
			return "notB2c";
		}
		if (coupon.accountValidity === CouponAccountValidity.ForB2b && Auth.customerType !== CustomerType.B2b) {
			return "notFound";
		}
		const products = Cart.orderProducts.filter((orderProduct) => {
			const subcategory = subcategories.find(({ id }) => id === orderProduct.info.subcategoryId);
			return coupon.subcategorySlugs.includes(subcategory?.urlSlug.cs ?? "");
		});
		if (products.length === 0) {
			return "noEligibleItems";
		}
		Cart.saveOrderFields({ couponCode: code });
		return true;
	});

	let couponInput = $state<HTMLInputElement>();

	async function apply(): Promise<void> {
		const result = await validateCoupon.call(currentCouponValue.trim());
		couponInput?.setCustomValidity(typeof result === "string" ? translate(result) : "");
		couponInput?.reportValidity();
	}

	function remove(): void {
		Cart.saveOrderFields({ couponCode: undefined });
	}
</script>

<div class={["flex flex-col gap-2", customClass]}>
	<span class="text-fcrgray text-sm/6 font-medium">
		<Formatted text={translate("label")} />
	</span>
	{#if Cart.couponCode !== undefined}
		<div class="flex flex-wrap items-center justify-between gap-3 bg-white rounded-3xl p-4">
			<span class="text-fcrdarkgray font-medium">
				<Formatted text={translate("appliedTitle", { code: Cart.couponCode })} />
			</span>
			<Button variant={ButtonVariant.Outlined} size={ButtonSize.Medium} type="button" onclick={remove}>
				<Formatted text={translate("remove")} />
			</Button>
		</div>
	{:else}
		<div class="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-2 items-center">
			<div class="contents sm:block lg:contents xl:block min-h-26">
				<Input
					name="couponCode"
					type="text"
					bind:input={couponInput}
					bind:value={currentCouponValue}
					oninput={(): void => {
						if (validateCoupon.value.status !== "loading" && couponInput) {
							couponInput.setCustomValidity("");
							couponInput.reportValidity();
						}
					}}
					placeholder={translate("placeholder")}
					autocomplete="off"
					wrapperClass="grow"
					maxlength={64}
				/>
			</div>

			<Button
				class="mb-6"
				variant={ButtonVariant.Dark}
				size={ButtonSize.Medium}
				type="button"
				loading={validateCoupon.value.status === "loading"}
				disabled={currentCouponValue.trim() === ""}
				onclick={apply}
			>
				<Formatted text={translate("apply")} />
			</Button>
		</div>
	{/if}
</div>
